import { AUDIENCE_AGE_OPTIONS, CHUZI_AI_VIBE_LABELS, t } from "@chuzi/shared/config";
import type { AudienceAge, RealmId } from "@chuzi/shared/types";

export interface StoryGeneratorContext {
  vibes: string[];
  character_count?: number;
  details: string;
  audience_age?: AudienceAge;
}

export interface GeneratorPrefs {
  vibes: string[];
  audience_age?: AudienceAge;
}

const PREFS_KEY = "chuzi:generatorPrefs";

function storageKey(storyId: string) {
  return `chuzi:storyGenerator:${storyId}`;
}

export function persistStoryGeneratorContext(storyId: string, context: StoryGeneratorContext) {
  try {
    localStorage.setItem(storageKey(storyId), JSON.stringify(context));
  } catch { /* quota — non-critical */ }
  persistGeneratorPrefs({ vibes: context.vibes, audience_age: context.audience_age });
}

export function loadStoryGeneratorContext(storyId: string): StoryGeneratorContext | null {
  try {
    const raw = localStorage.getItem(storageKey(storyId));
    if (!raw) return null;
    return JSON.parse(raw) as StoryGeneratorContext;
  } catch {
    return null;
  }
}

export function persistGeneratorPrefs(prefs: GeneratorPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch { /* quota — non-critical */ }
}

export function loadGeneratorPrefs(): GeneratorPrefs | null {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as GeneratorPrefs;
  } catch {
    return null;
  }
}

/**
 * Compose a scene brief from explicit vibe/audience/details inputs.
 * Used by the scene panel to build context behind the scenes.
 */
export function composeSceneBrief(
  realm: RealmId,
  vibes: string[],
  audienceAge: AudienceAge | "" | undefined,
  userDirection: string,
): string {
  const parts: string[] = [];
  if (vibes.length > 0) {
    const labels = vibes.map((vibe) => {
      const key = vibe as keyof typeof CHUZI_AI_VIBE_LABELS;
      return CHUZI_AI_VIBE_LABELS[key] ?? vibe;
    });
    parts.push(
      `[MANDATORY TONE] The genre/tone for this scene MUST be: ${labels.join(", ")}. This is a hard constraint — write in this style above all else. Do NOT deviate into unrelated tones or moods.`,
    );
  }
  if (audienceAge) {
    const option = AUDIENCE_AGE_OPTIONS.find((o) => o.id === audienceAge);
    const desc = option ? option.description : "";
    const wordLimits: Record<string, string> = {
      "4+": "CRITICAL: Keep the scene prose under 60 words total. Use only simple vocabulary a 4-year-old would understand. Sentences must be 5-8 words max. Make it fun, silly, and whimsical. No darkness, no peril, no complex emotions. Think picture-book language.",
      "7+": "CRITICAL: Keep the scene prose under 100 words total. Use easy vocabulary suitable for early readers. Sentences under 12 words. Keep it playful, light, and age-appropriate. No scary or dark themes.",
      "10+": "Keep the scene prose under 160 words. Use grade-school vocabulary. Some suspense is okay but keep it age-appropriate.",
      "13+": "Keep the scene prose under 250 words. Teen-level language and moderate complexity allowed.",
      "17+": "Keep the scene prose under 350 words. Full adult vocabulary and mature themes allowed.",
    };
    const wordDirective = wordLimits[audienceAge] ?? "";
    parts.push(
      `[MANDATORY AUDIENCE] Target audience is ${audienceAge}${desc ? ` — ${desc}` : ""}. ALL content must be appropriate for this age group. Do NOT include themes, language, or intensity that exceeds this rating.\n${wordDirective}`,
    );
  }
  if (userDirection.trim()) {
    parts.push(userDirection.trim());
  }
  parts.push(
    t(
      realm,
      "chuzi_ai_scene_continue_hook",
      "Write the next scene continuing from where the story left off. Do not end or resolve the story.",
    ),
  );
  return parts.join("\n");
}

/** Compose a scene-continue brief from stored generator input + story context. */
export function buildSceneContinueBrief(storyId: string, realm: RealmId): string {
  const ctx = loadStoryGeneratorContext(storyId);
  if (!ctx) {
    return t(
      realm,
      "chuzi_ai_scene_continue_default",
      "Continue the story with the next scene. Leave the narrative open-ended.",
    );
  }

  const parts: string[] = [];
  if (ctx.vibes.length > 0) {
    const labels = ctx.vibes.map((vibe) => {
      const key = vibe as keyof typeof CHUZI_AI_VIBE_LABELS;
      return CHUZI_AI_VIBE_LABELS[key] ?? vibe;
    });
    parts.push(
      t(realm, "chuzi_ai_scene_continue_vibes", "Stay in the same tone and genre ({vibes}).").replace(
        "{vibes}",
        labels.join(", "),
      ),
    );
  }
  if (ctx.character_count && ctx.character_count > 0) {
    parts.push(
      t(realm, "chuzi_ai_scene_continue_characters", "Keep roughly {count} characters in play.").replace(
        "{count}",
        String(ctx.character_count),
      ),
    );
  }
  if (ctx.audience_age) {
    parts.push(
      `Write at a reading level appropriate for ${ctx.audience_age} audiences.`,
    );
  }
  if (ctx.details.trim()) {
    parts.push(ctx.details.trim());
  }
  parts.push(
    t(
      realm,
      "chuzi_ai_scene_continue_hook",
      "Write the next scene continuing from where the story left off. Do not end or resolve the story.",
    ),
  );
  return parts.join(" ");
}
